/* @flow */
import * as React from 'react';
import css from '@styled-system/css';
import styled from 'styled-components';

import { Box } from '@sonyged/proc-ui';

type Props = {
  children: React.Node,
  dWidth: number,
  dHeight: number,
  dMinWidth: number,
  dMinHeight: number,
  align?: 'center' | 'top' | 'bottom',
  onScaleChange?: Function,
};

export function ScalableContainer(props: Props) {
  const { children, onScaleChange, align = 'center', dWidth, dHeight, dMinWidth, dMinHeight, ...rest } = props;

  const ref = React.useRef();

  const [{ w, h, x, y }, setState] = React.useState({ w: 0, h: 0, x: 0, y: 0 });

  const reScale = React.useCallback(() => {
    const div = ref.current;
    if (!div) return;

    const viewWidth = div.clientWidth;
    const viewHeight = div.clientHeight;
    const scale = Math.min(
      Math.max(viewHeight / dHeight, viewWidth / dWidth),
      viewWidth / dMinWidth,
      viewHeight / dMinHeight
    );

    const w = scale * dWidth;
    const h = scale * dHeight;
    const x = (viewWidth - w) / 2;
    const y = (viewHeight - h) / 2;
    setState({ w, h, x, y });
    if (onScaleChange) onScaleChange(scale);
  }, [dWidth, dHeight, dMinWidth, dMinHeight, onScaleChange]);

  React.useLayoutEffect(() => {
    window.addEventListener('resize', reScale);
    reScale();

    return () => window.removeEventListener('resize', reScale);
  }, [reScale]);

  const offsetStyle = React.useMemo(
    () => ({
      width: w,
      height: h,
      top: align === 'top' ? 0 : null,
      bottom: align === 'bottom' ? 0 : null,
      transform: align === 'center' ? `translate3d(${x}px, ${y}px, 0)` : `translate3d(${x}px, 0, 0)`,
    }),
    [x, y, w, h, align]
  );

  return (
    <Box {...rest} ref={ref} position="relative" overflow="hidden">
      <Container style={offsetStyle}>{children}</Container>
    </Box>
  );
}

const Container = styled(Box)(
  css({
    position: 'absolute',
    margin: 'auto',
  })
);
